import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Users, Shield, ClipboardList, CircleAlert, ArrowUpRight, ChevronRight, Sparkles, Plus, CalendarDays } from "lucide-react";
import api from "../lib/api";
import { formatShort, daysUntil, initials } from "../lib/format";
import TaskDialog from "../components/TaskDialog";

const statBlocks = [
  { key: "clients", label: "Clientes ativos", icon: Users, color: "blue", sub: "Gestão completa" },
  { key: "weapons", label: "Armas cadastradas", icon: Shield, color: "green", sub: "CRAFs monitorados" },
  { key: "open_processes", label: "Processos em andamento", icon: ClipboardList, color: "amber", sub: "Regularização" },
  { key: "attention", label: "Exigem atenção", icon: CircleAlert, color: "red", sub: "Vencem em 60 dias" },
];

export default function Dashboard() {
  const nav = useNavigate();
  const [data, setData] = useState({ stats: { clients:0, weapons:0, open_processes:0, attention:0 }, events: [], clients: [] });
  const [taskOpen, setTaskOpen] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/dashboard");
      setData(data);
    } catch {}
  };

  useEffect(() => {
    load();
    const fn = () => load();
    window.addEventListener("crafguard:refresh", fn);
    return () => window.removeEventListener("crafguard:refresh", fn);
  }, []);

  const now = new Date();
  const weekdays = ["DOM","SEG","TER","QUA","QUI","SEX","SÁB"];
  const days = Array.from({length:7}, (_,i)=>{
    const d = new Date(now); d.setDate(now.getDate()+i);
    return { n: String(d.getDate()).padStart(2,"0"), w: weekdays[d.getDay()], iso: d.toISOString().slice(0,10) };
  });

  const empty = data.stats.clients === 0 && data.stats.weapons === 0 && data.stats.open_processes === 0;

  return (
    <>
      <div className="page-heading">
        <div>
          <span className="eyebrow">{new Date().toLocaleDateString("pt-BR",{weekday:"long", day:"2-digit", month:"short", year:"numeric"}).toUpperCase()}</span>
          <h1>Bom dia, {data && data.clients?.[0] ? "consultor" : "consultor"}.</h1>
          <p>Aqui está o resumo da sua operação hoje.</p>
        </div>
        <button data-testid="new-task-button" className="primary-button" onClick={()=>setTaskOpen(true)}>
          <Plus size={17}/> Nova tarefa
        </button>
      </div>

      <section className="stat-grid">
        {statBlocks.map(({key,label,icon:Icon,color,sub}) => (
          <div className="stat" data-testid={`stat-${key}`} key={key}>
            <div className={`stat-icon ${color}`}><Icon size={20}/></div>
            <div>
              <span>{label}</span>
              <strong>{data.stats[key]}</strong>
              <small className={color}>{sub} <ArrowUpRight size={12}/></small>
            </div>
          </div>
        ))}
      </section>

      {empty ? (
        <section className="panel empty-panel">
          <div className="empty-illustration"><Sparkles size={26}/></div>
          <h2>Comece cadastrando seu primeiro cliente</h2>
          <p>Ou carregue os dados demonstrativos pela barra lateral para explorar todas as funcionalidades.</p>
          <div style={{display:"flex", gap:12, justifyContent:"center", marginTop:18, flexWrap:"wrap"}}>
            <button data-testid="empty-add-client" className="primary-button" onClick={()=>nav("/clients")}><Plus size={16}/> Novo cliente</button>
            <button data-testid="empty-view-agenda" className="outline-button" style={{width:"auto", marginTop:0}} onClick={()=>nav("/agenda")}><CalendarDays size={16}/> Abrir agenda</button>
          </div>
        </section>
      ) : (
        <div className="section-grid">
          <section className="panel calendar-panel">
            <div className="panel-head">
              <div><span className="eyebrow">PRÓXIMOS 7 DIAS</span><h2>Agenda de vencimentos</h2></div>
              <button data-testid="view-agenda-button" className="text-button" onClick={()=>nav("/agenda")}>Ver agenda <ChevronRight size={15}/></button>
            </div>
            <div className="calendar-strip">
              {days.map((d,i)=>{
                const hasEvent = data.events.some(e => e.date === d.iso);
                const urgent = data.events.some(e => e.date === d.iso && e.type === "Urgente");
                return (
                  <div key={d.iso} className={i===0 ? "day today" : "day"}>
                    <small>{d.w}</small><b>{d.n}</b>
                    {hasEvent && <i className={urgent ? "" : "blue-dot"}/>}
                  </div>
                );
              })}
            </div>
            <div className="event-list">
              {data.events.slice(0,5).map(e => (
                <div className="event-row" data-testid={`event-${e.id}`} key={e.id}>
                  <div className={`event-dot ${(e.type||"").toLowerCase()}`}/>
                  <div className="event-info">
                    <b>{e.title}</b>
                    <span>{e.client} · {e.detail || "—"}</span>
                  </div>
                  <time>{formatShort(e.date)}</time>
                </div>
              ))}
              {!data.events.length && <div className="event-empty">Sem eventos nos próximos dias.</div>}
            </div>
          </section>
          <section className="panel attention-panel">
            <div className="panel-head">
              <div><span className="eyebrow">AÇÃO NECESSÁRIA</span><h2>Itens em atenção</h2></div>
              <span className="count-badge">{String(data.stats.attention).padStart(2,"0")}</span>
            </div>
            {data.events.filter(e=>e.type==="Urgente").slice(0,4).map(e => (
              <div className="attention-item" key={e.id}>
                <div className="warning"><CircleAlert size={18}/></div>
                <div>
                  <b>{e.title} · {daysUntil(e.date)} dias</b>
                  <span>{e.client} · {e.detail || ""}</span>
                </div>
                <ChevronRight size={16}/>
              </div>
            ))}
            {!data.events.some(e=>e.type==="Urgente") && (
              <div className="attention-empty">Sem urgências no radar. </div>
            )}
            <button data-testid="review-alerts-button" className="outline-button" onClick={()=>nav("/agenda")}>Revisar pendências <ArrowUpRight size={15}/></button>
          </section>
        </div>
      )}

      {!empty && (
        <section className="panel clients-panel">
          <div className="panel-head">
            <div><span className="eyebrow">CARTEIRA</span><h2>Clientes recentes</h2></div>
            <button data-testid="view-clients-button" className="text-button" onClick={()=>nav("/clients")}>Todos os clientes <ChevronRight size={15}/></button>
          </div>
          <div className="client-table">
            <div className="table-row table-header">
              <span>CLIENTE</span><span>ARMAS</span><span>PROCESSOS</span><span>STATUS</span><span/>
            </div>
            {data.clients.slice(0,6).map(c => (
              <div className="table-row" data-testid={`client-row-${c.id}`} key={c.id} onClick={()=>nav(`/clients?open=${c.id}`)}>
                <div className="client-cell">
                  <div className="avatar avatar-light">{initials(c.name)}</div>
                  <div>
                    <b>{c.name}</b>
                    <small>{c.document || "—"}</small>
                  </div>
                </div>
                <span>{c.weapons_count ?? 0}</span>
                <span>{c.processes_count ?? 0}</span>
                <span><label className="status">Ativo</label></span>
                <ChevronRight size={16}/>
              </div>
            ))}
          </div>
        </section>
      )}

      <TaskDialog open={taskOpen} onClose={()=>setTaskOpen(false)} onSaved={load}/>
    </>
  );
}
